import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { listingsAPI, categoriesAPI } from '@/lib/api';

const EditListingDialog = ({ listing, open, onOpenChange, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category_id: '',
        subcategory_id: '',
        condition: 'good',
        shipping_cost: '',
        return_policy: 'no_returns',
        location: '',
        accepts_trades: false,
    });

    // Load categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await categoriesAPI.getAll();
                setCategories(res.data || []);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);

    // Initialize form data when listing changes
    useEffect(() => {
        if (listing && open) {
            setFormData({
                title: listing.title || '',
                description: listing.description || '',
                category_id: listing.category_id || '',
                subcategory_id: listing.subcategory_id || '',
                condition: listing.condition || 'good',
                shipping_cost: listing.shipping_cost?.toString() || '0',
                return_policy: listing.return_policy || 'no_returns',
                location: listing.location || '',
                accepts_trades: listing.accepts_trades || false,
            });
            
            // Find and set the selected category
            const cat = categories.find(c => c.category_id === listing.category_id);
            setSelectedCategory(cat || null);
        }
    }, [listing, open, categories]);

    // Update selected category when category_id changes
    useEffect(() => {
        if (formData.category_id) {
            const cat = categories.find(c => c.category_id === formData.category_id);
            setSelectedCategory(cat || null);
        }
    }, [formData.category_id, categories]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Reset subcategory when category changes
        if (field === 'category_id') {
            setFormData(prev => ({ ...prev, subcategory_id: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            toast.error('Titlul este obligatoriu');
            return;
        }
        
        setLoading(true);
        try {
            const payload = {
                title: formData.title,
                description: formData.description,
                category_id: formData.category_id || null,
                subcategory_id: formData.subcategory_id || null,
                condition: formData.condition,
                shipping_cost: formData.shipping_cost ? parseFloat(formData.shipping_cost) : 0,
                return_policy: formData.return_policy,
                location: formData.location,
                accepts_trades: formData.accepts_trades,
            };

            await listingsAPI.update(listing.listing_id, payload);
            toast.success('Anunțul a fost actualizat cu succes');
            onOpenChange(false);
            if (onSuccess) onSuccess();
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Eroare la actualizarea anunțului');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent 
                className="max-w-lg max-h-[90vh] overflow-y-auto md:max-w-lg md:max-h-[90vh] max-md:w-screen max-md:h-screen max-md:max-w-none max-md:max-h-none max-md:rounded-none max-md:m-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>Editare Anunț</DialogTitle>
                    <DialogDescription>
                        Modifică detaliile anunțului tău.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {/* Title */}
                    <div className="space-y-2">
                        <Label htmlFor="edit-title">Titlu *</Label>
                        <Input
                            id="edit-title"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder="Titlul anunțului"
                            required
                        />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="edit-description">Descriere</Label>
                        <Textarea
                            id="edit-description"
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Descriere detaliată..."
                            rows={3}
                        />
                    </div>

                    {/* Category & Subcategory */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Categorie</Label>
                            <Select
                                value={formData.category_id}
                                onValueChange={(value) => handleChange('category_id', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selectează" />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((cat) => (
                                        <SelectItem key={cat.category_id} value={cat.category_id}>
                                            {cat.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Subcategorie</Label>
                            <Select
                                value={formData.subcategory_id || 'none'}
                                onValueChange={(value) => handleChange('subcategory_id', value === 'none' ? '' : value)}
                                disabled={!selectedCategory?.subcategories?.length}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selectează" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Fără subcategorie</SelectItem>
                                    {selectedCategory?.subcategories?.map((sub) => (
                                        <SelectItem key={sub.subcategory_id} value={sub.subcategory_id}>
                                            {sub.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Condition & Location */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Stare</Label>
                            <Select
                                value={formData.condition}
                                onValueChange={(value) => handleChange('condition', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="mint">Mint (Nou)</SelectItem>
                                    <SelectItem value="excellent">Excelentă</SelectItem>
                                    <SelectItem value="very_good">Foarte bună</SelectItem>
                                    <SelectItem value="good">Bună</SelectItem>
                                    <SelectItem value="fair">Acceptabilă</SelectItem>
                                    <SelectItem value="poor">Slabă</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Locație</Label>
                            <Input
                                value={formData.location}
                                onChange={(e) => handleChange('location', e.target.value)}
                                placeholder="Ex: București"
                            />
                        </div>
                    </div>

                    {/* Shipping & Return */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Cost transport (RON)</Label>
                            <Input
                                type="number"
                                value={formData.shipping_cost}
                                onChange={(e) => handleChange('shipping_cost', e.target.value)}
                                placeholder="0"
                                min="0"
                                step="0.01"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Politica retur</Label>
                            <Select
                                value={formData.return_policy}
                                onValueChange={(value) => handleChange('return_policy', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="no_returns">Fără retur</SelectItem>
                                    <SelectItem value="7_days">7 zile</SelectItem>
                                    <SelectItem value="14_days">14 zile</SelectItem>
                                    <SelectItem value="30_days">30 zile</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Accepts Trades */}
                    <div className="flex items-center space-x-2 pt-2">
                        <input
                            type="checkbox"
                            id="edit-accepts-trades"
                            checked={formData.accepts_trades}
                            onChange={(e) => handleChange('accepts_trades', e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            data-testid="edit-accepts-trades-checkbox"
                        />
                        <Label htmlFor="edit-accepts-trades" className="cursor-pointer">Accept schimburi</Label>
                    </div>

                    <DialogFooter className="pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={loading}
                        >
                            Anulează
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Salvează
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EditListingDialog;
